import * as BatchAuditsActions from './batch-audit.actions';

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {BatchAudit, BatchAuditSearchParams, BatchOpResult} from '@accounting/_common/models/batch-audit.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const batchAuditsFeatureKey = 'batchAudits';

export function sortByDate(a: BatchAudit, b: BatchAudit) {
    if (a.date !== b.date) {
        return StoredComponentHelperService.sortArrayComparer(a, b, 'date');
    }

    return (a.id || '').localeCompare(b.id || '');
}

export const adapter: EntityAdapter<BatchAudit> = createEntityAdapter<BatchAudit>({
    sortComparer: sortByDate
});

export interface BatchAuditsState extends EntityStateModel<BatchAudit, BatchAuditSearchParams> {
    currentBatchOperation?: BatchOpResult
    dialogOpened?: boolean
}

export const initialState: BatchAuditsState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: [],
});

export const batchAuditsReducer = createReducer(
    initialState,

    on(BatchAuditsActions.CheckState, (state, action) => {
        if (action.payload.modalView) {
            return adapter.removeAll({
                ...state,
                savedFilter: !action.payload.close && state.filter ? state.filter : undefined,
                pagination: initialState.pagination,
                filter: action.payload.close ? state.savedFilter : undefined
            });
        } else {
            if (action.payload.close) {
                return adapter.setAll(action.payload.items, {
                    ...state,
                    pagination: {...state.pagination, number: 0},
                    componentOpened: false
                });
            }

            return {...state, componentOpened: true};
        }
    }),

    on(BatchAuditsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(BatchAuditsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(BatchAuditsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(BatchAuditsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(BatchAuditsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(BatchAuditsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(BatchAuditsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(BatchAuditsActions.SetCurrentBatchOperation, (state, action) => ({
        ...state,
        ...action.payload.dialogOpened != undefined && {dialogOpened: action.payload.dialogOpened},
        currentBatchOperation: action.payload.batchOperation
    })),

    on(BatchAuditsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();
